<template>
  <div class="agency-report-home">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <v-icon class="title-icon" color="#fff">mdi-chart-bar</v-icon>
            Reports Dashboard
          </h1>
          <p class="page-subtitle">Generate and download detailed reports for your volunteer activities</p>
        </div>
        <div class="header-stats">
          <v-chip color="white" outlined class="stat-chip">
            <v-icon left color="#ffff">mdi-file-document</v-icon>
            <span style="color: #ffff; font-weight: 600;">Total Reports: {{ filteredItems.length }}</span>
          </v-chip>
        </div>
      </div>
    </div>
<!-- Filters Card -->
<v-card class="filters-card" elevation="3">
  <v-card-title class="filters-title">
    <v-icon left color="#fff">mdi-filter-variant</v-icon>
    Filter Reports
    <v-spacer></v-spacer>
    <v-chip color="white" outlined small>
      <v-icon left small>mdi-information</v-icon>
      Select at least one filter
    </v-chip>
  </v-card-title>

  <v-card-text class="pa-6">
    <!-- Volunteer Filter - Full Width -->
    <v-row>
      <v-col cols="12">
        <v-select
          v-model="selectedVolunteer"
          :items="volunteers"
          item-text="text"
          item-value="value"
          label="Select Volunteer"
          outlined
          dense
          :return-object="true"
          ref="volunteerSelect"
          :error="!!volunteerMessage"
          :error-messages="volunteerMessage"
          prepend-inner-icon="mdi-account"
          clearable
          @click:clear="clearFilter('volunteer')"
        >
          <template v-slot:selection="{ item }">
            <v-chip small color="primary" text-color="white">
              <v-icon left small>mdi-account</v-icon>
              {{ item.text }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <!-- Need Filter - Full Width -->
 <!-- Need Filter - Full Width -->
<v-row>
  <v-col cols="12">
    <v-select
      v-model="selectedNeed"
      :items="needsList"
      item-text="title"
      item-value="_id"
      label="Select Need"
      outlined
      dense
      ref="needSelect"
      :error="!!needMessage"
      :error-messages="needMessage"
      prepend-inner-icon="mdi-hand-heart"
      clearable
      @click:clear="clearFilter('need')"
    >
      <template v-slot:selection="{ item }">
        <v-chip small color="success" text-color="white">
          <v-icon left small>mdi-hand-heart</v-icon>
          {{ item.title }}
        </v-chip>
      </template>
    </v-select>
  </v-col>
</v-row>

    <!-- Date Range Filters - Full Width with From and To in same row -->
    <v-row>
      <v-col cols="12">
        <div class="date-range-wrapper">
          <v-menu
            v-model="fromDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="
                  fromDate
                    ? formatDate(fromDate)
                    : ''
                "
                label="From Date"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                ref="fromDateInput"
                :error="!!fromMessage"
                :error-messages="fromMessage"
                prepend-inner-icon="mdi-calendar-start"
                clearable
                @click:clear="clearFilter('fromDate')"
                class="date-field"
              />
            </template>
            <v-date-picker
              v-model="fromDate"
              :max="new Date().toISOString().substr(0, 10)"
              color="primary"
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="fromDatePicker = false">
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="
                  fromDatePicker = false;
                  if (!fromDate)
                    fromDate = new Date().toISOString().substr(0, 10);
                "
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>

          <span class="date-separator">to</span>

          <v-menu
            v-model="toDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="
                  toDate
                    ? formatDate(toDate)
                    : ''
                "
                label="To Date"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                ref="toDateInput"
                :error="!!toMessage"
                :error-messages="toMessage"
                prepend-inner-icon="mdi-calendar-end"
                clearable
                @click:clear="clearFilter('toDate')"
                class="date-field"
              />
            </template>
            <v-date-picker
              v-model="toDate"
              :max="new Date().toISOString().substr(0, 10)"
              color="primary"
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="toDatePicker = false">
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="
                  toDatePicker = false;
                  if (!toDate)
                    toDate = new Date().toISOString().substr(0, 10);
                "
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </div>
      </v-col>
    </v-row>

    <!-- Active Filters Summary -->
    <div v-if="hasActiveFilters" class="active-filters-summary mt-4">
      <v-divider class="mb-4"></v-divider>
      <div class="d-flex align-center flex-wrap">
        <span class="font-weight-medium mr-3">Active Filters:</span>
        <v-chip
          v-if="selectedVolunteer"
          close
          @click:close="clearFilter('volunteer')"
          color="primary"
          text-color="white"
          small
          class="mr-2 mb-1"
        >
          <v-icon left small>mdi-account</v-icon>
          {{ selectedVolunteer.text }}
        </v-chip>
        <v-chip
          v-if="selectedNeed"
          close
          @click:close="clearFilter('need')"
          color="success"
          text-color="white"
          small
          class="mr-2 mb-1"
        >
          <v-icon left small>mdi-hand-heart</v-icon>
          {{ getNeedTitle(selectedNeed) }}
        </v-chip>
        <v-chip
          v-if="fromDate"
          close
          @click:close="clearFilter('fromDate')"
          color="info"
          text-color="white"
          small
          class="mr-2 mb-1"
        >
          <v-icon left small>mdi-calendar-start</v-icon>
          From: {{ formatDate(fromDate) }}
        </v-chip>
        <v-chip
          v-if="toDate"
          close
          @click:close="clearFilter('toDate')"
          color="info"
          text-color="white"
          small
          class="mr-2 mb-1"
        >
          <v-icon left small>mdi-calendar-end</v-icon>
          To: {{ formatDate(toDate) }}
        </v-chip>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons mt-4">
      <v-btn
        color="primary"
        class="download-btn"
        :loading="downloading"
        :disabled="downloading || !canDownload"
        @click="filterReports"
        large
        elevation="2"
      >
        <v-icon left>{{ downloading ? '' : 'mdi-file-pdf-box' }}</v-icon>
        {{ downloading ? 'Generating Report...' : 'Download PDF Report' }}
      </v-btn>

      <v-btn
        color="secondary"
        class="clear-btn ml-3"
        @click="clearFilters"
        outlined
        large
      >
        <v-icon left>mdi-close-circle</v-icon>
        Clear All
      </v-btn>

      <!-- Quick Stats -->
      <v-spacer></v-spacer>
      <div class="quick-stats">
        <v-chip color="primary" outlined class="mr-2">
          <v-icon left small>mdi-account-group</v-icon>
          Volunteers: {{ volunteers.length }}
        </v-chip>
        <v-chip color="success" outlined>
          <v-icon left small>mdi-hand-heart</v-icon>
          Needs: {{ needs.length }}
        </v-chip>
      </div>
    </div>
  </v-card-text>
</v-card>

    <!-- Custom Loading Overlay - Only covers content area -->
    <div v-if="downloading" class="loading-overlay-wrapper">
      <div class="loading-overlay-content">
        <div class="loader-container text-center">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
            width="4"
            class="mb-4"
          ></v-progress-circular>
          <h3 class="white--text">Generating Report...</h3>
          <p class="white--text text-subtitle-2">Please wait while we prepare your report</p>
          <div class="loader-animation">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div class="mt-4">
            <v-progress-linear
              indeterminate
              color="primary"
              height="4"
              class="rounded"
            ></v-progress-linear>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <v-card v-if="filteredItems.length > 0" class="results-card mt-5" elevation="3">
      <v-card-title class="results-title">
        <v-icon left color="#fff">mdi-format-list-bulleted</v-icon>
        Report Details
        <v-spacer></v-spacer>
        <v-chip color="white" outlined>
          <v-icon left small>mdi-file-document</v-icon>
          {{ filteredItems.length }} Record{{ filteredItems.length > 1 ? 's' : '' }}
        </v-chip>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          class="elevation-1 report-table"
          :items-per-page="10"
          :loading="loading"
          loading-text="Loading report data..."
        >
          <template v-slot:top>
            <v-toolbar flat class="table-toolbar">
              <v-toolbar-title class="text-h6">
                <v-icon left>mdi-file-chart</v-icon>
                Needs Report
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="downloadExcel" color="success">
                <v-icon>mdi-microsoft-excel</v-icon>
              </v-btn>
              <v-btn icon @click="downloadPDF" color="primary">
                <v-icon>mdi-file-pdf</v-icon>
              </v-btn>
            </v-toolbar>
          </template>

          <!-- Custom S.No column -->
          <template v-slot:item.sno="{ index }">
            <span class="font-weight-medium">{{ index + 1 }}</span>
          </template>

          <!-- Custom Status/Progress column -->
          <template v-slot:item.volunteersWorked="{ item }">
            <v-chip
              :color="item.volunteersWorked > 0 ? 'success' : 'grey'"
              text-color="white"
              small
            >
              {{ item.volunteersWorked > 0 ? 'Active' : 'No Activity' }}
            </v-chip>
          </template>

          <!-- Custom Worked Hours -->
          <template v-slot:item.workedHours="{ item }">
            <span class="font-weight-medium">
              <v-icon small color="primary">mdi-clock-outline</v-icon>
              {{ item.workedHours }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Empty State -->
    <v-card
      v-else-if="!loading && filteredItems.length === 0 && hasSearched"
      class="empty-state-card mt-5"
      elevation="3"
    >
      <v-card-text class="text-center pa-8">
        <v-img
          src="@/assets/svg/no-need.svg"
          height="200"
          contain
          class="mb-4"
        />
        <h3 class="empty-title mb-3">No Reports Found</h3>
        <p class="empty-subtitle mb-4">
          Try adjusting your filters or select different criteria
        </p>
        <v-btn color="primary" @click="clearFilters">
          <v-icon left>mdi-filter-remove</v-icon>
          Clear Filters
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      top
      right
      elevation="6"
    >
      <v-icon left color="white">{{ snackbarIcon }}</v-icon>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text color="white" v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "AgencyReportHome",
  
  data() {
    return {
      // Filters
      fromDate: "",
      toDate: "",
      fromDatePicker: false,
      toDatePicker: false,
      selectedVolunteer: null,
      selectedNeed: null,
      
      // Table
      headers: [
        { text: "S.No", value: "sno", sortable: false, width: "80px" },
        { text: "Need Title", value: "needTitle", sortable: true },
        { text: "Status", value: "volunteersWorked", sortable: true },
        { text: "Worked Hours", value: "workedHours", sortable: true },
        { text: "Start Date", value: "startDate", sortable: true },
        { text: "End Date", value: "endDate", sortable: true }
      ],
      
      items: [],
      filteredItems: [],
      volunteers: [],
      
      // Validation Messages
      volunteerMessage: null,
      needMessage: null,
      fromMessage: null,
      toMessage: null,
      
      // UI State
      snackbar: false,
      snackbarColor: "error",
      snackbarIcon: "mdi-alert",
      snackbarTimeout: 5000,
      errorMessage: "",
      downloading: false,
      loading: false,
      hasSearched: false
    };
  },

  computed: {
    ...mapGetters("need", {
      needs: "getAllNeeds"
    }),
    
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),

      needsList() {
    // If needs is an array, return it
    if (Array.isArray(this.needs)) {
      return this.needs;
    }
    // If needs has a 'res' property (API response structure)
    if (this.needs && this.needs.res && Array.isArray(this.needs.res)) {
      return this.needs.res;
    }
    // If needs has a 'data' property
    if (this.needs && this.needs.data && Array.isArray(this.needs.data)) {
      return this.needs.data;
    }
    // Return empty array if nothing found
    return [];
  },

    hasActiveFilters() {
      return !!(
        this.selectedVolunteer ||
        this.selectedNeed ||
        this.fromDate ||
        this.toDate
      );
    },

    canDownload() {
      return (
        this.selectedVolunteer ||
        this.selectedNeed ||
        (this.fromDate && this.toDate)
      );
    }
  },

  methods: {
    ...mapActions("need", {
      getVolunteerList: "getVolunteerList",
      getPdfData: "getPdfData",
      fetchAllNeeds: "fetchAllNeeds"
    }),

    // Core Methods
    async filterReports() {
      // Clear previous errors
      this.clearErrors();

      // Validate date range
      if ((this.fromDate && !this.toDate) || (!this.fromDate && this.toDate)) {
        if (!this.fromDate) {
          this.$refs.fromDateInput.focus();
          this.fromMessage = "Please select a valid From date.";
        }
        if (!this.toDate) {
          this.$refs.toDateInput.focus();
          this.toMessage = "Please select a valid To date.";
        }
        this.showSnackbar("Both From and To dates must be selected.", "error");
        return;
      }

      // Validate date logic
      if (this.fromDate && this.toDate && new Date(this.fromDate) > new Date(this.toDate)) {
        this.$refs.fromDateInput.focus();
        this.fromMessage = "From date cannot be later than To date.";
        this.showSnackbar("From date cannot be later than To date.", "error");
        return;
      }

      // Validate at least one filter
      if (!this.selectedVolunteer && !this.selectedNeed && !this.fromDate && !this.toDate) {
        this.showSnackbar("Please select at least one filter.", "warning");
        return;
      }

      // Start loading
      this.downloading = true;
      this.loading = true;

      try {
        const params = {
          fromDate: this.fromDate || null,
          toDate: this.toDate || null,
          volunteerId: this.selectedVolunteer?.value || null,
          needId: this.selectedNeed || null
        };

        const response = await this.getPdfData(params);
        
        console.log("Filtered Data:", response);

        // Process response data
        if (response && response.length > 0) {
          this.filteredItems = response.map((item, index) => ({
            sno: index + 1,
            needTitle: item.needTitle || item.title || "Untitled Need",
            volunteersWorked: item.totalWorkedHours > 0 || item.totalWorkedMinutes > 0 ? 1 : 0,
            workedHours: `${item.totalWorkedHours || 0}h ${item.totalWorkedMinutes || 0}m`,
            startDate: item.fromDate 
              ? new Date(item.fromDate).toLocaleDateString('en-US')
              : "N/A",
            endDate: item.toDate
              ? new Date(item.toDate).toLocaleDateString('en-US')
              : "N/A"
          }));
          
          this.hasSearched = true;
          this.showSnackbar(
            `Report generated successfully! Found ${this.filteredItems.length} records.`,
            "success",
            "mdi-check-circle"
          );
        } else {
          this.filteredItems = [];
          this.hasSearched = true;
          this.showSnackbar(
            "No records found for the selected filters.",
            "info",
            "mdi-information"
          );
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
        this.showSnackbar(
          "Failed to generate report. Please try again.",
          "error",
          "mdi-alert"
        );
        this.filteredItems = [];
      } finally {
        this.downloading = false;
        this.loading = false;
      }
    },

    downloadExcel() {
      this.showSnackbar("Excel download feature coming soon!", "info", "mdi-information");
    },

    downloadPDF() {
      this.showSnackbar("PDF download feature coming soon!", "info", "mdi-information");
    },

    // Filter Management
    clearFilter(type) {
      switch (type) {
        case "volunteer":
          this.selectedVolunteer = null;
          this.volunteerMessage = null;
          break;
        case "need":
          this.selectedNeed = null;
          this.needMessage = null;
          break;
        case "fromDate":
          this.fromDate = "";
          this.fromMessage = null;
          break;
        case "toDate":
          this.toDate = "";
          this.toMessage = null;
          break;
        default:
          break;
      }
    },

    clearFilters() {
      this.fromDate = "";
      this.toDate = "";
      this.selectedVolunteer = null;
      this.selectedNeed = null;
      this.filteredItems = [];
      this.hasSearched = false;
      this.clearErrors();
      this.showSnackbar("All filters have been cleared.", "info", "mdi-information");
    },

    clearErrors() {
      this.volunteerMessage = null;
      this.needMessage = null;
      this.fromMessage = null;
      this.toMessage = null;
    },

    // Volunteer List
    async volunteerList() {
      try {
        const response = await this.getVolunteerList();
        if (response && response.fanned) {
          this.volunteers = response.fanned.map(volunteer => ({
            text: `${volunteer.first_name} ${volunteer.last_name}`,
            value: volunteer._id
          }));
        } else if (Array.isArray(response)) {
          this.volunteers = response.map(volunteer => ({
            text: `${volunteer.first_name} ${volunteer.last_name}`,
            value: volunteer._id
          }));
        }
      } catch (error) {
        console.error("Error fetching volunteer list:", error);
        this.showSnackbar(
          "Failed to load volunteer list.",
          "error",
          "mdi-alert"
        );
      }
    },

    getNeedTitle(needId) {
      const need = this.needs.find(n => n._id === needId);
      return need ? need.title : "Unknown Need";
    },

    // Utility Methods
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    },

    // Snackbar Helper
    showSnackbar(message, color = "error", icon = "mdi-alert") {
      this.errorMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    }
  },

  mounted() {
    this.volunteerList();
      // If needs is empty, fetch needs
  if (this.needsList.length === 0) {
    this.fetchNeeds();
  }
  }
};
</script>

<style scoped>
.agency-report-home {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  position: relative;
}

/* Header Section */
.header-section {
  margin-bottom: 24px;
  padding: 24px;
  border-radius: 20px;
  background: linear-gradient(135deg, #084468, #0a5b8a);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #ffff;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  font-size: 2rem !important;
}

.page-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.header-stats .stat-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

/* Filters Card */
.filters-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.filters-title {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  color: white;
  padding: 16px 24px;
}

/* Date Range */
.date-range-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-field {
  flex: 1;
}

.date-separator {
  font-weight: 600;
  color: #084468;
  padding: 0 4px;
}

/* Active Filters */
.active-filters-summary {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 12px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.download-btn {
  min-width: 200px;
  font-weight: 600 !important;
}

.clear-btn {
  font-weight: 600 !important;
}

.quick-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Custom Loading Overlay - Only covers content */
.loading-overlay-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;
}

.loading-overlay-content {
  position: absolute;
  top: 64px; /* Adjust based on your navbar height */
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.loader-container {
  padding: 40px 60px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 350px;
  max-width: 90%;
  pointer-events: all;
  animation: fadeInUp 0.3s ease;
}

.loader-container h3 {
  font-weight: 600;
  margin-bottom: 8px;
  color: #fff;
}

.loader-container p {
  color: rgba(255, 255, 255, 0.8);
}

/* Loader Animation */
.loader-animation {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.loader-animation span {
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loader-animation span:nth-child(1) {
  animation-delay: -0.32s;
}

.loader-animation span:nth-child(2) {
  animation-delay: -0.16s;
}

.loader-animation span:nth-child(3) {
  animation-delay: 0s;
}

.loader-animation span:nth-child(4) {
  animation-delay: 0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Results Card */
.results-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.results-title {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  color: white;
  padding: 16px 24px;
}

.table-toolbar {
  background: #f8f9fa;
  padding: 0 16px;
}

.report-table {
  border-radius: 0;
}

.report-table:deep(.v-data-table-header) {
  background: #e9ecef !important;
}

.report-table:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: #084468 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

/* Empty State */
.empty-state-card {
  border-radius: 16px !important;
  background: #f8f9fa;
}

.empty-title {
  color: #2c3e50;
  font-weight: 600;
}

.empty-subtitle {
  color: #7f8c8d;
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 960px) {
  .agency-report-home {
    padding: 16px;
  }
  
  .page-title {
    font-size: 1.75rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-stats {
    text-align: center;
  }
  
  .date-range-wrapper {
    flex-wrap: wrap;
  }
  
  .date-separator {
    width: 100%;
    text-align: center;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .download-btn,
  .clear-btn {
    width: 100%;
    margin-left: 0 !important;
  }
  
  .quick-stats {
    justify-content: center;
    margin-top: 8px;
  }

  .loading-overlay-content {
    top: 56px; /* Adjust for mobile navbar */
  }
  
  .loader-container {
    padding: 30px 20px;
    min-width: auto;
    width: 90%;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.5rem;
  }
  
  .filters-card .v-card-text {
    padding: 16px !important;
  }
  
  .active-filters-summary .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 4px;
  }
  
  .active-filters-summary .v-chip {
    width: 100%;
  }
  
  .results-title {
    flex-direction: column;
    gap: 8px;
  }
  
  .loader-container {
    padding: 25px 16px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .active-filters-summary {
    background: #ffffff00;
  }
  
  .table-toolbar {
    background: #2c3e50;
  }
  
  .report-table:deep(.v-data-table-header) {
    background: #34495e !important;
  }
  
  .empty-state-card {
    background: #5badff00;
  }
  
  .empty-title {
    color: #ecf0f1;
  }
  
  .empty-subtitle {
    color: #bdc3c7;
  }
}
</style>